/**
 * Positions API helper — WI-15c.
 *
 * Fetches the position taxonomy for a given sport slug from
 * GET /api/v1/positions?sport=<slug> and maps the response to the existing
 * SportPositionTaxonomy interface shape.
 *
 * Two-layer caching:
 *   1. HTTP cache (Cache-Control: max-age=3600 on the server response).
 *   2. Module-level in-memory Map — second calls for the same sport slug
 *      return the cached value without a network request.
 *
 * COARSE_TOOLTIP_COPY is intentionally NOT fetched from the backend and NOT
 * included in the returned SportPositionTaxonomy. Tooltip copy stays
 * frontend-only in constants/positions.ts (resolved 2026-05-04). Callers
 * that need tooltip copy merge it from the local SPORT_TOOLTIP_COPY map.
 */

import { SportSlug, SportPositionTaxonomy } from '../constants/positions';

// ── In-memory cache ───────────────────────────────────────────────────────────

/** Module-level cache. Second calls for the same sport return this without a fetch. */
const taxonomyCache = new Map<SportSlug, SportPositionTaxonomy>();

/**
 * Exposed for testing only — allows tests to pre-populate the cache so that
 * components that call fetchPositionTaxonomy get synchronous-like behavior.
 * Do NOT call this in production application code.
 */
export function _testOnlySetCache(sport: SportSlug, taxonomy: SportPositionTaxonomy): void {
  taxonomyCache.set(sport, taxonomy);
}

/**
 * Exposed for testing only — clears the in-memory cache between tests.
 * Do NOT call this in production application code.
 */
export function _testOnlyClearCache(): void {
  taxonomyCache.clear();
}

// ── API response shape (from backend) ────────────────────────────────────────

interface PositionsApiResponse {
  sport: string;
  coarse_order: string[];
  fine_under_coarse: Record<string, string[]>;
  display_names: Record<string, string>;
  full_names: Record<string, string>;
}

// ── Mapper ───────────────────────────────────────────────────────────────────

/**
 * Maps the backend's snake_case JSON to the existing SportPositionTaxonomy
 * interface shape. Does NOT set COARSE_TOOLTIP_COPY — callers are responsible
 * for merging tooltip copy from constants/positions.ts.
 */
function mapResponseToTaxonomy(raw: PositionsApiResponse): SportPositionTaxonomy {
  return {
    COARSE_ORDER: raw.coarse_order,
    FINE_UNDER_COARSE: raw.fine_under_coarse,
    POSITION_DISPLAY_NAMES: raw.display_names,
    POSITION_FULL_NAMES: raw.full_names,
    // COARSE_TOOLTIP_COPY is intentionally absent from the API response.
    // Callers must merge from SPORT_TOOLTIP_COPY in constants/positions.ts.
    COARSE_TOOLTIP_COPY: {},
  };
}

// ── fetchPositionTaxonomy ─────────────────────────────────────────────────────

/**
 * Fetches GET /api/v1/positions?sport=<slug> and returns a SportPositionTaxonomy.
 *
 * Second calls for the same sport slug return the cached value without a
 * network request. The returned taxonomy does NOT include COARSE_TOOLTIP_COPY —
 * callers must merge from the local constants if needed.
 *
 * Throws if the fetch fails or the response is not OK.
 */
export async function fetchPositionTaxonomy(sport: SportSlug): Promise<SportPositionTaxonomy> {
  const cached = taxonomyCache.get(sport);
  if (cached) return cached;

  const resp = await fetch(`/api/v1/positions?sport=${sport}`);
  if (!resp.ok) {
    throw new Error(`positions fetch failed for ${sport}: HTTP ${resp.status}`);
  }
  const raw: PositionsApiResponse = await resp.json();
  const taxonomy = mapResponseToTaxonomy(raw);
  taxonomyCache.set(sport, taxonomy);
  return taxonomy;
}

// ── prefetchAllTaxonomies ─────────────────────────────────────────────────────

/** All sport slugs known to the frontend. Add a new slug here when a new sport
 * is added to the DB (alongside a migration for that sport's positions rows). */
const KNOWN_SPORT_SLUGS: SportSlug[] = [
  'soccer_mens',
  'soccer_womens',
  'football_mens',
  'basketball_mens',
  'basketball_womens',
];

/**
 * Pre-fetches position taxonomies for all known sport slugs in parallel.
 *
 * Uses Promise.allSettled so individual fetch failures are non-blocking —
 * the returned Promise always resolves even if some sport fetches fail.
 * Failed sports will be missing from the cache, triggering an on-demand
 * fetch when the user selects that sport.
 *
 * Called once on App mount (fire-and-forget). Errors are swallowed here;
 * components handle missing-cache states with skeleton + error UI.
 */
export async function prefetchAllTaxonomies(): Promise<void> {
  await Promise.allSettled(
    KNOWN_SPORT_SLUGS.map((slug) => fetchPositionTaxonomy(slug))
  );
}

/**
 * Synchronously returns the cached taxonomy for a sport slug, or null if
 * not yet fetched. Used by components to read taxonomy without triggering
 * a new fetch (they call fetchPositionTaxonomy separately for the async path).
 */
export function getCachedTaxonomy(sport: SportSlug): SportPositionTaxonomy | null {
  return taxonomyCache.get(sport) ?? null;
}
